/**
 * Hints Panel Component
 * Displays dynamic, personalized hints and tips based on user progress
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { getNextAction } from '../../utils/gamificationActions';


interface HintsPanelProps {
  userGamification: {
    totalXP: number;
    readinessPercentage: number;
    completedTasks: string[];
  };
  nextTask?: {
    id: string;
    title: string;
    description: string;
    xpReward: number;
  } | null;
  onDismiss?: () => void;
  compact?: boolean;
}

export const HintsPanel: React.FC<HintsPanelProps> = ({
  userGamification,
  nextTask,
  onDismiss,
  compact = false,
}) => {
  const navigate = useNavigate();

  // Generate dynamic hint based on user's progress
  const currentHint = useMemo(
    () =>
      getNextAction(
        userGamification.completedTasks,
        userGamification.readinessPercentage
      ),
    [userGamification.completedTasks, userGamification.readinessPercentage]
  );

  const handleAction = () => {
    navigate(currentHint.actionUrl);
  };

  return (
    <div className={`bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100 relative ${compact ? 'p-2' : 'p-3'}`}>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/50 rounded-full transition-colors"
          aria-label="Dismiss hint"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      )}

      <div className="flex items-start space-x-2">
        <span className={`${compact ? 'text-lg' : 'text-xl'} flex-shrink-0`}>{currentHint.emoji}</span>
        <div className="flex-1">
          <h4 className="text-xs font-semibold text-gray-900 mb-1 flex items-center">
            <Sparkles className="w-3 h-3 mr-1 text-purple-600" />
            Next Step
          </h4>
          <p className="text-xs text-gray-700 mb-2 leading-snug">{currentHint.message}</p>

          {nextTask && !compact && (
            <div className="bg-white/60 rounded-md border border-blue-100 p-2 mb-2">
              <p className="text-xs font-medium text-gray-800">
                {nextTask.title}
              </p>
              <p className="text-[10px] text-gray-600 mt-0.5">
                +{nextTask.xpReward} XP
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleAction}
              className={`inline-flex items-center bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm ${compact ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
            >
              {currentHint.actionText}
              <ArrowRight className="w-3 h-3 ml-1" />
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className={`bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors ${compact ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
              >
                Remind me later
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HintsPanel;
